import { Injectable, inject } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { map, Observable } from 'rxjs';
import { ApiService } from '../api/api.service';

export interface OrgMediaAssetUsage {
    id?: string | null;
    target_type: 'org' | 'project' | 'property' | 'phase' | string;
    target_id: string;
    slot_key: string;
    target_label?: string | null;
    meta_json?: any | null;
    created_at?: string | null;
    updated_at?: string | null;
}

export interface OrgMediaAsset {
    id: string;
    org_id: string;
    file_id: string;
    display_name?: string | null;
    description?: string | null;
    content_type?: string | null;
    size_bytes?: number | null;
    is_public: boolean;
    bucket?: string | null;
    object_key?: string | null;
    file_name?: string | null;
    preview_url?: string | null;
    usage_count?: number;
    created_at?: string | null;
    updated_at?: string | null;
}

@Injectable({ providedIn: 'root' })
export class OrgMediaService {
    private api = inject(ApiService);

    private withOrgId(params: HttpParams, orgId?: string | null): HttpParams {
        const id = String(orgId || '').trim();
        return id ? params.set('org_id', id) : params;
    }

    listAssets(visibility?: 'public' | 'private' | '', orgId?: string | null): Observable<OrgMediaAsset[]> {
        let params = new HttpParams();
        const filter = String(visibility || '').trim().toLowerCase();
        if (filter) params = params.set('visibility', filter);
        params = this.withOrgId(params, orgId);
        return this.api.get<any>('/API2/org-media-assets', params).pipe(
            map((resp) => resp?.data?.items || resp?.items || []),
            map((items) => (Array.isArray(items) ? items : []) as OrgMediaAsset[])
        );
    }

    getAsset(assetId: string, orgId?: string | null): Observable<OrgMediaAsset | null> {
        const params = this.withOrgId(new HttpParams(), orgId);
        return this.api.get<any>(`/API2/org-media-assets/${encodeURIComponent(assetId)}`, params).pipe(
            map((resp) => (resp?.data?.asset || resp?.asset || null) as OrgMediaAsset | null)
        );
    }

    getUsages(assetId: string, orgId?: string | null): Observable<OrgMediaAssetUsage[]> {
        const params = this.withOrgId(new HttpParams(), orgId);
        return this.api.get<any>(`/API2/org-media-assets/${encodeURIComponent(assetId)}/usages`, params).pipe(
            map((resp) => resp?.data?.usages || resp?.usages || []),
            map((items) => (Array.isArray(items) ? items : []) as OrgMediaAssetUsage[])
        );
    }

    createAsset(payload: { file_id: string; display_name?: string | null; description?: string | null; org_id?: string | null }): Observable<OrgMediaAsset | null> {
        return this.api.post<any>('/API2/org-media-assets', payload).pipe(
            map((resp) => (resp?.data?.asset || resp?.asset || null) as OrgMediaAsset | null)
        );
    }

    updateAsset(
        assetId: string,
        payload: { display_name?: string | null; description?: string | null; is_public?: boolean; org_id?: string | null }
    ): Observable<OrgMediaAsset | null> {
        const params = this.withOrgId(new HttpParams(), payload?.org_id);
        return this.api.patch<any>(`/API2/org-media-assets/${encodeURIComponent(assetId)}`, payload, params).pipe(
            map((resp) => (resp?.data?.asset || resp?.asset || null) as OrgMediaAsset | null)
        );
    }

    assignAsset(
        assetId: string,
        payload: {
            target_type: 'org' | 'project' | 'property' | 'phase' | string;
            target_id: string;
            slot_key: string;
            is_main?: boolean;
            sort_order?: number | null;
            alt?: string | null;
            org_id?: string | null;
        }
    ): Observable<{ asset: OrgMediaAsset | null; usages: OrgMediaAssetUsage[] }> {
        return this.api.post<any>(`/API2/org-media-assets/${encodeURIComponent(assetId)}/assignments`, payload).pipe(
            map((resp) => ({
                asset: (resp?.data?.asset || resp?.asset || null) as OrgMediaAsset | null,
                usages: (resp?.data?.usages || resp?.usages || []) as OrgMediaAssetUsage[]
            }))
        );
    }

    unassignAsset(
        assetId: string,
        payload: { target_type: 'org' | 'project' | 'property' | 'phase' | string; target_id: string; slot_key: string; org_id?: string | null }
    ): Observable<{ asset: OrgMediaAsset | null; usages: OrgMediaAssetUsage[] }> {
        return this.api.post<any>(`/API2/org-media-assets/${encodeURIComponent(assetId)}/unassign`, payload).pipe(
            map((resp) => ({
                asset: (resp?.data?.asset || resp?.asset || null) as OrgMediaAsset | null,
                usages: (resp?.data?.usages || resp?.usages || []) as OrgMediaAssetUsage[]
            }))
        );
    }

    deleteAsset(assetId: string, orgId?: string | null): Observable<any> {
        const params = this.withOrgId(new HttpParams(), orgId);
        return this.api.delete<any>(`/API2/org-media-assets/${encodeURIComponent(assetId)}`, params);
    }
}
